// Liste des tâches d'un dossier, groupée par urgence (en retard, cette
// semaine, plus tard, terminées), avec la charge par intervenant à droite.
import { useMemo, useState, type ReactNode } from 'react';
import { TaskTypeDot } from '../tasks/TaskTypeDot';
import { createPortal } from 'react-dom';
import { cn } from '../../lib/cn';
import type { Task } from '../../hooks/queries/useTasks';
import { useTaskCompletion } from '../../hooks/useTaskCompletion';
import { useTasksStore } from '../../store/tasksStore';
import { rangeSelection } from '../../lib/tasks/taskSelection';
import { TaskDoneCircle, TaskSelectBox } from '../tasks/TaskRowControls';
import { useUndoableTaskDelete } from '../../hooks/useUndoableTaskDelete';
import { useCanAccessFeature } from '../../hooks/usePermissions';
import { FaTrashCan } from '../ui/FaIcons';
import { useTasksAssignments } from '../../hooks/queries/useTaskCollaboration';
import {
  TASK_URGENCY_GROUPS,
  groupTasksByUrgency,
  relativeDueLabel,
  taskDaysLeft,
  taskDueIso,
  type TaskUrgencyGroup,
} from '../../lib/tasks/urgencyGroups';
import { formatAppDate } from '../../lib/dateFormat';
import { Avatar } from '../ui/Avatar';
import { ListEmpty } from '../ui/ListToolbar';
import { TaskPreviewCard } from '../tasks/TaskPreviewCard';
import { useTaskHoverPreview } from '../../hooks/queries/useCabinetSettings';
import { useTaskTagDisplay } from '../../hooks/useTaskTagDisplay';
import { useTaskTagsFor } from '../../hooks/queries/useTaskTags';
import { TaskTagStripe } from '../tasks/TaskTagStripe';
import type { TaskTag } from '../../services/tasks/taskTagService';
import { buildTaskPreview } from '../tasks/taskPreview';
import { useTaskListPreview } from '../tasks/useTaskListPreview';
import { getDueDate } from '../tasks/taskHelpers';
import { extractDueTime } from '../../lib/tasks/taskReschedule';
import { TaskContextMenu, useTaskContextMenu } from '../tasks/TaskContextMenu';

interface Props {
  tasks: Task[];
  /** Montre aussi le groupe « Terminées ». */
  showDone: boolean;
  /** Ne garde que les tâches assignées à cette personne. */
  onlyAssigneeId?: string | null;
  onOpenTask: (task: Task) => void;
  /** Barre de saisie et de filtres, posée en tête de la colonne des tâches. */
  toolbar?: ReactNode;
  /** Suspend la fiche au survol (une fenêtre est ouverte). */
  previewPaused?: boolean;
}

export function DossierTasksUrgencyList({ tasks, showDone, onlyAssigneeId, onOpenTask, toolbar, previewPaused = false }: Props) {
  const taskIds = useMemo(() => tasks.map((t) => t.id), [tasks]);
  const { data: assignments } = useTasksAssignments(taskIds);
  const completion = useTaskCompletion();
  const requestDelete = useUndoableTaskDelete();
  const canDelete = useCanAccessFeature('tasks_delete');
  const selectedTaskIds = useTasksStore((s) => s.selectedTaskIds);
  const selectionAnchorId = useTasksStore((s) => s.selectionAnchorId);
  const toggleSelected = useTasksStore((s) => s.toggleSelected);
  const selectMany = useTasksStore((s) => s.selectMany);
  const [doneOpen, setDoneOpen] = useState(false);
  const now = useMemo(() => new Date(), []);

  const visible = useMemo(
    () =>
      onlyAssigneeId
        ? tasks.filter((t) => assignments?.get(t.id)?.some((p) => p.id === onlyAssigneeId))
        : tasks,
    [tasks, assignments, onlyAssigneeId],
  );
  const groups = useMemo(() => groupTasksByUrgency(visible, now), [visible, now]);

  const load = useMemo(() => {
    const byPerson = new Map<string, { name: string; avatar: string | null; open: number; late: number }>();
    for (const task of tasks) {
      if (task.is_completed) continue;
      const days = taskDaysLeft(task, now);
      for (const person of assignments?.get(task.id) ?? []) {
        const row = byPerson.get(person.id) ?? { name: person.full_name, avatar: person.avatar_url, open: 0, late: 0 };
        row.open += 1;
        if (days !== null && days < 0) row.late += 1;
        byPerson.set(person.id, row);
      }
    }
    return [...byPerson.entries()].sort((a, b) => b[1].open - a[1].open);
  }, [tasks, assignments, now]);
  const maxOpen = Math.max(1, ...load.map(([, row]) => row.open));


  const menu = useTaskContextMenu();
  const hoverPreview = useTaskHoverPreview();
  // Cette liste n'affiche pas les étiquettes en pastilles : elle ne les charge qu'en mode bordure.
  const bordered = useTaskTagDisplay() === 'border';
  const { data: tagMap = {} } = useTaskTagsFor(bordered ? tasks.map((t) => t.id) : []);
  const preview = useTaskListPreview(!hoverPreview || previewPaused || menu.target !== null);
  const hovered = preview.hoverId ? tasks.find((t) => t.id === preview.hoverId) ?? null : null;
  const previewModel = hovered
    ? buildTaskPreview({
        title: hovered.content,
        description: hovered.description,
        isCompleted: hovered.is_completed,
        dueIso: getDueDate(hovered),
        dueTime: extractDueTime(hovered.due_datetime),
        deadlineIso: hovered.deadline,
        dossierTitle: null,
        priority: null,
        sectionName: null,
        tags: [],
        people: (assignments?.get(hovered.id) ?? []).map((person) => ({
          id: person.id,
          name: person.full_name?.trim() || 'Intervenant',
          avatarUrl: person.avatar_url,
        })),
        commentCount: 0,
        attachmentCount: 0,
      }, now)
    : null;

  // Les 2 dernières tâches terminées restent visibles quand le groupe « Terminées » est masqué (demande du cabinet).
  const recentDone = useMemo(
    () =>
      showDone
        ? []
        : [...groups.done]
            .sort((a, b) => new Date(b.completed_at ?? 0).getTime() - new Date(a.completed_at ?? 0).getTime())
            .slice(0, 2),
    [groups, showDone],
  );

  const shownGroups = TASK_URGENCY_GROUPS.filter(
    (group) => groups[group.key].length > 0 && (group.key !== 'done' || showDone),
  );

  // Maj + clic étend la sélection dans l'ordre affiché, groupes repliés exclus.
  function select(id: string, shiftKey: boolean) {
    if (shiftKey && selectionAnchorId) {
      const orderedIds = [
        ...shownGroups
          .filter((group) => group.key !== 'done' || doneOpen)
          .flatMap((group) => groups[group.key].map((task) => task.id)),
        ...recentDone.map((task) => task.id),
      ];
      const ids = rangeSelection(orderedIds, selectionAnchorId, id);
      if (ids.length > 0) {
        selectMany(ids, id);
        return;
      }
    }
    toggleSelected(id);
  }

  return (
    <div className="grid grid-cols-1 items-start gap-3.5 xl:grid-cols-[minmax(0,1fr)_330px]">
      <div className="flex min-w-0 flex-col gap-3">
        {toolbar}
        {shownGroups.length === 0 && recentDone.length === 0 ? (
          <div className="rounded-xl border border-neutral-200 bg-white">
            <ListEmpty>Aucune tâche à afficher.</ListEmpty>
          </div>
        ) : (
          shownGroups.map((group) => {
            const collapsed = group.key === 'done' && !doneOpen;
            return (
              <section key={group.key}>
                <GroupHeader
                  group={group.key}
                  label={group.label}
                  count={groups[group.key].length}
                  collapsed={collapsed}
                  onToggle={group.key === 'done' ? () => setDoneOpen((open) => !open) : undefined}
                />
                {collapsed ? null : (
                  <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
                    {groups[group.key].map((task) => (
                      <TaskLine
                        key={task.id}
                        task={task}
                        people={assignments?.get(task.id) ?? []}
                        stripeTags={bordered ? (tagMap[task.id] ?? []) : null}
                        now={now}
                        onOpen={() => onOpenTask(task)}
                        onToggle={() => completion.toggle(task)}
                        selected={selectedTaskIds.has(task.id)}
                        selectionActive={selectedTaskIds.size > 0}
                        onSelect={(shiftKey) => select(task.id, shiftKey)}
                        onDelete={canDelete ? () => requestDelete({ id: task.id, content: task.content }) : undefined}
                        onEnter={(el) => preview.onEnter(task.id, el)}
                        onLeave={preview.onLeave}
                        onMenu={(e) => menu.open(e, task)}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })
        )}
        {recentDone.length > 0 ? (
          <section>
            <GroupHeader group="done" label="Dernières terminées" count={recentDone.length} collapsed={false} />
            <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white">
              {recentDone.map((task) => (
                <TaskLine
                  key={task.id}
                  task={task}
                  showCompletedAt
                  people={assignments?.get(task.id) ?? []}
                  stripeTags={bordered ? (tagMap[task.id] ?? []) : null}
                  now={now}
                  onOpen={() => onOpenTask(task)}
                  onToggle={() => completion.toggle(task)}
                  selected={selectedTaskIds.has(task.id)}
                  selectionActive={selectedTaskIds.size > 0}
                  onSelect={(shiftKey) => select(task.id, shiftKey)}
                  onDelete={canDelete ? () => requestDelete({ id: task.id, content: task.content }) : undefined}
                  onEnter={(el) => preview.onEnter(task.id, el)}
                  onLeave={preview.onLeave}
                  onMenu={(e) => menu.open(e, task)}
                />
              ))}
            </div>
          </section>
        ) : null}
      </div>

      <aside className="rounded-xl border border-neutral-200 bg-white">
        <header className="border-b border-[#e6e2d8] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-cime-text-muted">
          Charge sur le dossier
        </header>
        {load.length === 0 ? (
          <p className="px-4 py-4 text-xs text-neutral-400">Aucune tâche ouverte assignée.</p>
        ) : (
          <div className="flex flex-col gap-3 px-4 py-3.5">
            {load.map(([id, row]) => (
              <div key={id}>
                <div className="flex items-center gap-2.5 text-[13px]">
                  <Avatar name={row.name} src={row.avatar} size="xs" className="h-6 w-6 text-[10px]" />
                  <span className="min-w-0 flex-1 leading-tight">
                    <span className="block truncate text-cime-text-primary">{row.name}</span>
                    <span className="block text-xs text-neutral-400">
                      {row.open} tâche{row.open > 1 ? 's' : ''}
                      {row.late > 0 ? ` · ${row.late} en retard` : ''}
                    </span>
                  </span>
                </div>
                <div className="mt-1.5 h-1 overflow-hidden rounded-full bg-neutral-100">
                  <i
                    className={cn('block h-full rounded-full', row.late > 0 ? 'bg-[#c8431f]' : 'bg-cime-primary')}
                    style={{ width: `${Math.round((row.open / maxOpen) * 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </aside>

      {previewModel && preview.rect && createPortal(
        <TaskPreviewCard key={preview.hoverId} model={previewModel} anchor={preview.rect} shown={preview.shown} />,
        document.body,
      )}
      {menu.target ? (
        <TaskContextMenu target={menu.target} onClose={menu.close} onOpenTask={onOpenTask} />
      ) : null}
    </div>
  );
}

function GroupHeader({
  group,
  label,
  count,
  collapsed,
  onToggle,
}: {
  group: TaskUrgencyGroup;
  label: string;
  count: number;
  collapsed: boolean;
  onToggle?: () => void;
}) {
  const content = (
    <>
      <svg
        viewBox="0 0 12 12"
        aria-hidden
        className={cn('h-3 w-3 transition-transform', collapsed && '-rotate-90')}
      >
        <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      {label}
      <span className="font-normal tabular-nums text-neutral-400">{count}</span>
    </>
  );
  const className = cn(
    'mb-1.5 flex items-center gap-1.5 px-1 text-xs font-semibold',
    group === 'late' ? 'text-[#c8431f]' : group === 'done' ? 'text-neutral-400' : 'text-cime-text-primary',
  );
  return onToggle ? (
    <button type="button" onClick={onToggle} aria-expanded={!collapsed} className={className}>
      {content}
    </button>
  ) : (
    <div className={className}>{content}</div>
  );
}

function TaskLine({
  task,
  people,
  now,
  onOpen,
  onToggle,
  selected,
  selectionActive,
  onSelect,
  onDelete,
  onEnter,
  onLeave,
  onMenu,
  stripeTags,
  showCompletedAt = false,
}: {
  task: Task;
  /** Affiche la date de fin (en vert) à la place de l'échéance. */
  showCompletedAt?: boolean;
  /** Étiquettes de la bande gauche, en mode bordure. null : mode pastilles. */
  stripeTags: TaskTag[] | null;
  people: { id: string; full_name: string; avatar_url: string | null }[];
  now: Date;
  onOpen: () => void;
  onToggle: () => void;
  selected: boolean;
  selectionActive: boolean;
  onSelect: (shiftKey: boolean) => void;
  onDelete?: () => void;
  onEnter: (el: HTMLElement) => void;
  onLeave: () => void;
  onMenu: (event: React.MouseEvent) => void;
}) {
  const done = !!task.is_completed;
  const iso = taskDueIso(task);
  const days = taskDaysLeft(task, now);
  const late = !done && days !== null && days < 0;

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onMouseEnter={(e) => onEnter(e.currentTarget)}
      onMouseLeave={onLeave}
      onContextMenu={onMenu}
      onKeyDown={(e) => {
        if (e.key === 'Enter') onOpen();
      }}
      className={cn(
        'group relative flex cursor-pointer items-center gap-3.5 border-b border-l-2 border-neutral-100 px-4 py-3 transition-colors last:border-b-0',
        selected ? 'bg-cime-primary/5' : 'hover:bg-neutral-50',
        // En mode bordure, le bord gauche porte les étiquettes : le retard reste lisible par la date en rouge.
        late && !stripeTags ? 'border-l-[#c8431f]' : 'border-l-transparent',
      )}
    >
      {stripeTags ? <TaskTagStripe tags={stripeTags} /> : null}
      <TaskDoneCircle done={done} onToggle={onToggle} />
      {/* Tâche terminée : coche et texte grisé, jamais barré. */}
      <span className={cn('min-w-0 flex-1 truncate text-sm font-medium', done ? 'text-neutral-400' : 'text-cime-text-primary')}>
        <TaskTypeDot templateId={task.task_template_id} />
        {task.content}
      </span>
      {people.length > 0 ? (
        <span className="flex flex-shrink-0 -space-x-1.5">
          {people.slice(0, 3).map((person) => (
            <Avatar
              key={person.id}
              name={person.full_name}
              src={person.avatar_url}
              size="xs"
              className="h-6 w-6 text-[10px] ring-1 ring-white"
            />
          ))}
        </span>
      ) : null}
      <span className="w-[76px] flex-shrink-0 text-right leading-tight">
        {showCompletedAt && task.completed_at ? (
          <span className="block text-[13px] text-[#2f7d5b]" title="Terminée le">
            {formatAppDate(task.completed_at, { year: false })}
          </span>
        ) : iso ? (
          <>
            <span className={cn('block text-[13px]', late ? 'font-semibold text-[#c8431f]' : done ? 'text-neutral-400' : 'text-cime-text-primary')}>
              {formatAppDate(iso, { year: false })}
            </span>
            {!done && days !== null ? (
              <small className={cn('block text-[11px]', late ? 'text-[#c8431f]' : 'text-neutral-400')}>
                {relativeDueLabel(days)}
              </small>
            ) : null}
          </>
        ) : (
          <span className="text-neutral-300">—</span>
        )}
      </span>
      {/* Même paire qu'en vue tableau : la case de sélection, puis la poubelle (suppression annulable). */}
      <span className="flex flex-shrink-0 items-center gap-2.5">
        <TaskSelectBox selected={selected} selectionActive={selectionActive} onToggle={onSelect} />
        {onDelete ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            className="text-neutral-400 opacity-0 transition hover:text-red-600 focus-visible:opacity-100 group-hover:opacity-100"
            aria-label="Supprimer la tâche"
            title="Supprimer la tâche"
          >
            <FaTrashCan className="h-4 w-4" />
          </button>
        ) : null}
      </span>
    </div>
  );
}
